import log from 'loglevel';

// Set default level to INFO
const defaultLevel = log.levels.INFO;

// Get log level from environment variable - check both backend and frontend env vars
const envLogLevel = (process.env.LOG_LEVEL || process.env.NEXT_PUBLIC_LOG_LEVEL)?.toLowerCase();

// Use environment variable if valid, otherwise use default
const validLevels = ['trace', 'debug', 'info', 'warn', 'error', 'silent'] as const;
type LogLevel = (typeof validLevels)[number];

const logLevel =
  envLogLevel && validLevels.includes(envLogLevel as LogLevel)
    ? (envLogLevel as log.LogLevelDesc)
    : defaultLevel;

log.setLevel(logLevel);

// Check if we're running in a browser environment
const isBrowser = typeof window !== 'undefined';

interface LogEntry {
  timestamp: string;
  message: string;
  module?: string;
  data?: any;
}

// Helper function to create structured log object for server-side
const createStructuredLog = (message: string, args: any[]): string => {
  const timestamp = new Date().toISOString();

  const logObject =
    args.length === 1 && typeof args[0] === 'object' && 'module' in args[0]
      ? {
          timestamp,
          message,
          module: args[0].module,
          data: args[0].data,
        }
      : {
          timestamp,
          message,
          data: args.length === 1 ? args[0] : args.length > 1 ? args : undefined,
        };

  return JSON.stringify(logObject);
};

// Helper function to format browser log as "MODULE message" with data object
const formatBrowserLog = (message: string, args: any[]): [string, ...any[]] => {
  // If using structured format (message + {module, data})
  if (args.length === 1 && typeof args[0] === 'object' && 'module' in args[0]) {
    const { module, data } = args[0];
    const formattedMessage = `${module} ${message}`;
    return data !== undefined ? [formattedMessage, data] : [formattedMessage];
  }

  // For other formats, just pass through
  return args.length > 0 ? [message, ...args] : [message];
};

// Check current log level to avoid unnecessary work
const shouldLog = (level: number): boolean => {
  return log.getLevel() <= level;
};

// Server-side logging functions
const serverLog = {
  info: (message: string, ...args: any[]) => {
    if (!shouldLog(log.levels.INFO)) return;
    const logData = createStructuredLog(message, args);
    log.info(logData);
  },
  warn: (message: string, ...args: any[]) => {
    if (!shouldLog(log.levels.WARN)) return;
    const logData = createStructuredLog(message, args);
    log.warn(logData);
  },
  error: (message: string, ...args: any[]) => {
    if (!shouldLog(log.levels.ERROR)) return;
    const logData = createStructuredLog(message, args);
    log.error(logData);
  },
  debug: (message: string, ...args: any[]) => {
    if (!shouldLog(log.levels.DEBUG)) return;
    const logData = createStructuredLog(message, args);
    log.debug(logData);
  },
  setLevel: (level: log.LogLevelDesc) => log.setLevel(level),
};

// Browser-side logging with "MODULE message" format
const browserLog = {
  info: (message: string, ...args: any[]) => {
    if (!shouldLog(log.levels.INFO)) return;
    const [formattedMessage, ...logArgs] = formatBrowserLog(message, args);
    console.info(formattedMessage, ...logArgs);
  },
  warn: (message: string, ...args: any[]) => {
    if (!shouldLog(log.levels.WARN)) return;
    const [formattedMessage, ...logArgs] = formatBrowserLog(message, args);
    console.warn(formattedMessage, ...logArgs);
  },
  error: (message: string, ...args: any[]) => {
    if (!shouldLog(log.levels.ERROR)) return;
    const [formattedMessage, ...logArgs] = formatBrowserLog(message, args);
    console.error(formattedMessage, ...logArgs);
  },
  debug: (message: string, ...args: any[]) => {
    if (!shouldLog(log.levels.DEBUG)) return;
    const [formattedMessage, ...logArgs] = formatBrowserLog(message, args);
    console.debug(formattedMessage, ...logArgs);
  },
  setLevel: (level: log.LogLevelDesc) => log.setLevel(level),
};

// Export the appropriate logger based on environment
const logger = isBrowser ? browserLog : serverLog;

export default logger;
